import React from 'react';
import { Alert, Box, Button, ButtonBase, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import colors from '../../assets/theme/colors';
import LogFormShell from '../../components/UI/LogFormShell';
import {
  approveProfileInboundEmailSender,
  createProfileInboundEmailAddress,
  getProfileInboundEmailState,
  rejectProfileInboundEmail,
  removeProfileInboundEmailSender,
} from '../../services/profileInboundEmailService';
import { PROFILE_INBOUND_EMAIL_OPEN_EVENT } from './profileInboundEmailOpenRequest';

const EMPTY_STATE = { address: '', status: 'not_configured', pendingEmails: [], approvedSenders: [], recentEmails: [] };

const ProfileInboundEmailCard = ({ childId = '', personName = '', canManage = false, openOnLoad = false, prominent = false, showLauncher = true, onClose }) => {
  const [open, setOpen] = React.useState(openOnLoad);
  const [emailState, setEmailState] = React.useState(EMPTY_STATE);
  const [status, setStatus] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [busyId, setBusyId] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const displayName = String(personName || 'this person').trim();

  const loadState = React.useCallback(async () => {
    if (!childId || !canManage) return;
    setStatus('loading');
    try {
      let result = await getProfileInboundEmailState(childId);
      if (!result?.address) {
        result = await createProfileInboundEmailAddress(childId);
      }
      setEmailState({ ...EMPTY_STATE, ...result });
      setStatus('ready');
    } catch (_error) {
      setStatus('error');
    }
  }, [canManage, childId]);

  React.useEffect(() => { loadState(); }, [loadState]);
  React.useEffect(() => {
    setOpen(Boolean(openOnLoad));
  }, [childId, openOnLoad]);
  React.useEffect(() => {
    const handleOpenRequest = (event) => {
      if (event?.detail?.childId === childId) setOpen(true);
    };
    window.addEventListener(PROFILE_INBOUND_EMAIL_OPEN_EVENT, handleOpenRequest);
    return () => window.removeEventListener(PROFILE_INBOUND_EMAIL_OPEN_EVENT, handleOpenRequest);
  }, [childId]);

  if (!canManage) return null;

  const createAddress = async () => {
    setError('');
    setStatus('creating');
    try {
      await createProfileInboundEmailAddress(childId);
      await loadState();
    } catch (createError) {
      setError(createError?.message || 'Could not create the forwarding address.');
      setStatus('ready');
    }
  };

  const runAction = async (key, action) => {
    setBusyId(key);
    setError('');
    try {
      await action();
      await loadState();
    } catch (actionError) {
      setError(actionError?.message || 'That change could not be completed.');
    } finally {
      setBusyId('');
    }
  };

  const copyAddress = async () => {
    if (!emailState.address || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    await navigator.clipboard.writeText(emailState.address);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const pendingCount = emailState.pendingEmails.length;
  const cardSubtitle = pendingCount
    ? `${pendingCount} sender${pendingCount === 1 ? '' : 's'} waiting for approval.`
    : emailState.address ? 'Send an email or attachment to Lifelog.' : 'Preparing the forwarding address.';

  return <>
    {showLauncher ? <Paper variant="outlined" sx={{ borderRadius: '18px', borderColor: prominent ? alpha(colors.semantic.sageDeep, 0.3) : colors.semantic.line, bgcolor: '#FFFFFF', overflow: 'hidden' }}>
      <ButtonBase onClick={() => setOpen(true)} sx={{ width: '100%', p: prominent ? 1.8 : 1.5, minHeight: prominent ? 76 : 'auto', textAlign: 'left', justifyContent: 'flex-start' }}>
        <Box sx={{ width: prominent ? 48 : 42, height: prominent ? 48 : 42, borderRadius: '14px', display: 'grid', placeItems: 'center', bgcolor: colors.semantic.sageMist, color: colors.semantic.sageDeep, flexShrink: 0 }}><EmailOutlinedIcon /></Box>
        <Box sx={{ ml: 1.2, flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: prominent ? '1rem' : '0.94rem', fontWeight: 650, color: colors.semantic.ink }}>Forward an email</Typography>
          <Typography sx={{ mt: 0.15, fontSize: '0.78rem', color: colors.semantic.ink2 }}>{cardSubtitle}</Typography>
        </Box>
        {status === 'loading' ? <CircularProgress size={20} /> : <ChevronRightRoundedIcon sx={{ color: colors.semantic.ink2 }} />}
      </ButtonBase>
    </Paper> : null}

    {open ? <LogFormShell open onClose={() => {
      setOpen(false);
      onClose?.();
    }} title="Forward an email" subtitle={`Emails sent here belong only to ${displayName}'s Care Profile.`} forceDrawer compactTitle>
      <Stack spacing={1.5}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!emailState.address ? <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '16px', borderColor: colors.semantic.line }}>
          <Typography sx={{ fontSize: '0.86rem', color: colors.semantic.ink2 }}>Create a private forwarding address for {displayName}.</Typography>
          <Button variant="contained" onClick={createAddress} disabled={status === 'creating' || status === 'loading'} sx={{ mt: 1.2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>{status === 'creating' ? 'Creating…' : 'Create forwarding address'}</Button>
        </Paper> : <Paper variant="outlined" sx={{ p: 1.4, borderRadius: '16px', borderColor: alpha(colors.semantic.sageDeep, 0.28), bgcolor: alpha(colors.semantic.sageMist, 0.55) }}>
          <Typography sx={{ fontSize: '0.74rem', color: colors.semantic.ink2 }}>Forward care emails to</Typography>
          <Typography sx={{ mt: 0.4, fontSize: '0.88rem', fontWeight: 500, color: colors.semantic.ink, overflowWrap: 'anywhere' }}>{emailState.address}</Typography>
          <Button onClick={copyAddress} startIcon={<ContentCopyRoundedIcon />} sx={{ mt: 0.8, px: 0, textTransform: 'none', fontWeight: 600 }}>{copied ? 'Copied' : 'Copy email address'}</Button>
        </Paper>}

        {pendingCount ? <Stack spacing={1}>
          <Typography component="h3" sx={{ fontSize: '0.92rem', fontWeight: 600, color: colors.semantic.ink }}>Sender approval needed</Typography>
          {emailState.pendingEmails.map((email) => <Paper key={email.id} variant="outlined" sx={{ p: 1.35, borderRadius: '16px', borderColor: colors.semantic.profileFlow.careStory.warningBorder, bgcolor: alpha(colors.semantic.profileFlow.careStory.warningBg, 0.55) }}>
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: colors.semantic.ink }}>{email.subject || 'Forwarded email'}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: colors.semantic.ink2 }}>{email.senderEmail}</Typography>
            {email.preview ? <Typography sx={{ mt: 0.8, fontSize: '0.8rem', color: colors.semantic.ink2 }}>{email.preview}</Typography> : null}
            {(email.attachments || []).map((attachment) => <Stack key={attachment.id || attachment.filename} direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.7 }}><AttachFileRoundedIcon sx={{ fontSize: 16 }} /><Typography sx={{ fontSize: '0.73rem' }}>{attachment.filename}</Typography></Stack>)}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} sx={{ mt: 1.1 }}>
              <Button variant="contained" disabled={busyId === email.id} onClick={() => runAction(email.id, () => approveProfileInboundEmailSender(childId, email.id))} sx={{ textTransform: 'none', boxShadow: 'none' }}>Approve sender and add email</Button>
              <Button color="inherit" disabled={busyId === email.id} onClick={() => runAction(email.id, () => rejectProfileInboundEmail(childId, email.id))} sx={{ textTransform: 'none' }}>Reject email</Button>
            </Stack>
          </Paper>)}
        </Stack> : null}

        {emailState.recentEmails.length ? <Stack spacing={0.8}>
          <Divider />
          <Typography component="h3" sx={{ pt: 0.6, fontSize: '0.88rem', fontWeight: 600, color: colors.semantic.ink }}>Recently received</Typography>
          {emailState.recentEmails.map((email) => {
            const hasIssues = email.status === 'captured_with_issues' || email.status === 'failed';
            const detail = email.status === 'failed'
              ? 'Could not be organized'
              : email.attachmentCount
                ? `${email.attachmentCount} attachment${email.attachmentCount === 1 ? '' : 's'} · ${email.careSourceIds?.length || 0} source${email.careSourceIds?.length === 1 ? '' : 's'} saved`
                : 'Email body saved';
            const timelineCount = Number(email.outcome?.timelineCount || 0);
            const reviewCount = Number(email.outcome?.reviewCount || 0);
            const categories = Array.isArray(email.outcome?.categories) ? email.outcome.categories : [];
            return <Paper key={email.id} variant="outlined" sx={{ p: 1.15, borderRadius: '14px', borderColor: hasIssues ? colors.semantic.profileFlow.careStory.warningBorder : colors.semantic.line }}>
              <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: colors.semantic.ink }}>{email.subject || 'Forwarded email'}</Typography>
              <Typography sx={{ mt: 0.2, fontSize: '0.74rem', color: hasIssues ? colors.semantic.profileFlow.careStory.warningText : colors.semantic.ink2 }}>{detail}</Typography>
              {email.receivedLocalDate ? <Typography sx={{ mt: 0.25, fontSize: '0.72rem', color: colors.semantic.ink2 }}>Received {email.receivedLocalDate}</Typography> : null}
              {timelineCount || reviewCount || categories.length ? <Typography sx={{ mt: 0.55, fontSize: '0.75rem', color: colors.semantic.ink }}>
                {[timelineCount ? `${timelineCount} Timeline ${timelineCount === 1 ? 'entry' : 'entries'}` : '', reviewCount ? `${reviewCount} to review` : '', categories.length ? categories.join(', ') : ''].filter(Boolean).join(' · ')}
              </Typography> : null}
              {!email.attachmentCount ? <Typography sx={{ mt: 0.45, fontSize: '0.72rem', color: colors.semantic.ink2 }}>No attachments received</Typography> : null}
              {(email.attachments || []).map((attachment) => {
                const added = attachment.status === 'processing' || attachment.status === 'captured';
                return <Typography key={attachment.id || attachment.filename} sx={{ mt: 0.35, fontSize: '0.72rem', color: added ? colors.semantic.ink2 : colors.semantic.profileFlow.careStory.warningText }}>
                  {attachment.filename}: {added ? 'Added to Documents' : attachment.reason}
                </Typography>;
              })}
            </Paper>;
          })}
        </Stack> : null}

        {emailState.approvedSenders.length ? <Stack spacing={0.6}>
          <Divider />
          <Typography component="h3" sx={{ pt: 0.6, fontSize: '0.88rem', fontWeight: 600, color: colors.semantic.ink }}>Approved senders</Typography>
          {emailState.approvedSenders.map((email) => <Stack key={email} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography sx={{ minWidth: 0, fontSize: '0.8rem', color: colors.semantic.ink2, overflowWrap: 'anywhere' }}>{email}</Typography>
            <Button color="inherit" disabled={busyId === email} onClick={() => runAction(email, () => removeProfileInboundEmailSender(childId, email))} sx={{ flexShrink: 0, textTransform: 'none', fontSize: '0.75rem' }}>Remove access</Button>
          </Stack>)}
        </Stack> : null}

        <Box component="ol" sx={{ pl: 2.5, m: 0, color: colors.semantic.ink2 }}>
          <Typography component="li" sx={{ mb: 0.6, fontSize: '0.82rem' }}>Forward a care email or attachment to the address above.</Typography>
          <Typography component="li" sx={{ mb: 0.6, fontSize: '0.82rem' }}>Lifelog preserves the original and organizes useful details.</Typography>
          <Typography component="li" sx={{ mb: 0.6, fontSize: '0.82rem' }}>Dates found in the email determine where Timeline events appear. An unclear date will wait for review.</Typography>
          <Typography component="li" sx={{ mb: 0.6, fontSize: '0.82rem' }}>A new sender&apos;s first email waits for approval.</Typography>
          <Typography component="li" sx={{ fontSize: '0.82rem' }}>This address stays the same if {displayName}&apos;s name changes.</Typography>
        </Box>
      </Stack>
    </LogFormShell> : null}
  </>;
};

export default ProfileInboundEmailCard;
